import { Box, Checkbox, Table as KlikTable } from '@lego/klik-ui';
import { ChevronDown } from '@lego/klik-ui/icons';
import { FlexRender, type RowData } from '@tanstack/react-table';
import type { ReactElement, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { Row } from './appTable';

type TableBodyRowProps<T extends RowData> = {
  enableRowSelection?: boolean;
  expandableRow?: (row: Row<T>) => ReactElement;
  getRowProps?: (rowData: Row<T>['original']) => { opacity?: number };
  index: number;
  onRowClick?: (rowData: Row<T>['original'], row: Row<T>) => void;
  row: Row<T>;
  rowLink?: (rowData: Row<T>['original']) => string;
  showStickyShadow: boolean;
};

const chevronStyle = (isExpanded: boolean) => ({
  transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
  transition: 'transform 0.2s ease',
});

const ExpandChevron = <T extends RowData>({ href, row }: { href?: string; row: Row<T> }) => {
  const content = <ChevronDown height="4" style={chevronStyle(row.getIsExpanded())} />;

  if (href) {
    return (
      <Link
        style={{
          alignItems: 'center',
          display: 'flex',
          height: '100%',
          justifyContent: 'center',
          padding: '0.6em',
        }}
        to={href}
      >
        {content}
      </Link>
    );
  }

  return (
    <Box alignItems="center" display="flex" height="100%" justifyContent="center" padding="0.6em">
      {content}
    </Box>
  );
};

const CellContent = ({ children, rowLink }: { children: ReactNode; rowLink?: string }) => {
  if (rowLink) {
    return (
      <Link style={{ display: 'block', padding: '0.6em 1em' }} to={rowLink}>
        {children}
      </Link>
    );
  }

  return <Box padding="0.6em 1em">{children}</Box>;
};

export const TableBodyRow = <T extends RowData>({
  enableRowSelection,
  expandableRow,
  getRowProps,
  index,
  onRowClick,
  row,
  rowLink,
  showStickyShadow,
}: TableBodyRowProps<T>) => {
  const href = rowLink?.(row.original);
  const rowProps = getRowProps?.(row.original);

  return (
    <>
      <KlikTable.Row
        backgroundColor={index % 2 === 1 ? 'slate.10' : 'transparent'}
        css={{
          transition: 'background-color 0.2s',
          '&:hover:not(:has([data-interactive]:hover))': {
            backgroundColor: 'whitesmoke',
          },
        }}
        cursor={onRowClick || rowLink || expandableRow ? 'pointer' : 'default'}
        onClick={
          onRowClick
            ? () => onRowClick(row.original, row)
            : expandableRow
              ? row.getToggleExpandedHandler()
              : undefined
        }
        opacity={rowProps?.opacity}
      >
        {enableRowSelection && (
          <KlikTable.Cell data-interactive padding="0.6em" textAlign="center" width="2.5rem">
            <Checkbox
              isChecked={row.getIsSelected()}
              isDisabled={!row.getCanSelect()}
              onChange={row.getToggleSelectedHandler()}
              size="sm"
            />
          </KlikTable.Cell>
        )}
        {expandableRow && (
          <KlikTable.Cell padding={0} width="2.5rem">
            <ExpandChevron href={href} row={row} />
          </KlikTable.Cell>
        )}
        {row.getVisibleCells().map((cell) => {
          const isSticky = cell.column.columnDef.meta?.isSticky;
          return (
            <KlikTable.Cell
              css={
                isSticky
                  ? {
                      backgroundColor: index % 2 === 1 ? 'var(--chakra-colors-slate-10)' : 'white',
                      transition: 'background-color 0.2s',
                      'tr:hover:not(:has([data-interactive]:hover)) &': {
                        backgroundColor: 'whitesmoke',
                      },
                    }
                  : {
                      maskImage:
                        'linear-gradient(to right, white 0%, white calc(100% - 1.5rem), transparent 100%)',
                    }
              }
              fontSize={['sm', 'md']}
              key={cell.id}
              maxWidth={`${cell.column.getSize()}px`}
              overflow="hidden"
              padding={0}
              width={`${cell.column.getSize()}px`}
              {...(isSticky && {
                position: 'sticky',
                right: 0,
                backgroundColor: undefined,
                zIndex: 1,
                boxShadow: showStickyShadow ? '-4px 0 8px -4px rgba(0, 0, 0, 0.1)' : 'none',
              })}
            >
              <CellContent rowLink={href}>
                <FlexRender cell={cell} />
              </CellContent>
            </KlikTable.Cell>
          );
        })}
      </KlikTable.Row>
      {row.getIsExpanded() && expandableRow && (
        <KlikTable.Row
          onClick={onRowClick ? () => onRowClick(row.original, row) : undefined}
          width="full"
        >
          <KlikTable.Cell colSpan={row.getVisibleCells().length + 1} px={0} py={3}>
            {expandableRow(row)}
          </KlikTable.Cell>
        </KlikTable.Row>
      )}
    </>
  );
};
